import { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Pencil, X, Tag, GripVertical, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import ImageUploadOrUrl from '../../components/ImageUploadOrUrl';

export default function AdminBenefits() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]); // [{category_id, name, order}]
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [showCats, setShowCats] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([api.get('/admin/benefits'), api.get('/admin/benefits/categories')]);
      setItems(b.data);
      setCats(c.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const d = modal.data;
    try {
      if (d.benefit_id) await api.patch(`/admin/benefits/${d.benefit_id}`, d);
      else await api.post('/admin/benefits', d);
      toast.success('Beneficio guardado');
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };
  const remove = async (id) => {
    if (!window.confirm('¿Eliminar beneficio?')) return;
    await api.delete(`/admin/benefits/${id}`);
    load();
  };

  // --- Category CRUD ---
  const createCat = async () => {
    const name = newCat.trim();
    if (!name) return;
    try {
      await api.post('/admin/benefits/categories', { name, order: cats.length });
      toast.success('Categoría creada');
      setNewCat('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear');
    }
  };
  const startEditCat = (c) => { setEditingCatId(c.category_id); setEditingCatName(c.name); };
  const saveEditCat = async (c) => {
    const name = editingCatName.trim();
    if (!name) return;
    try {
      await api.patch(`/admin/benefits/categories/${c.category_id}`, { name, order: c.order || 0 });
      toast.success('Categoría actualizada');
      setEditingCatId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
  };
  const deleteCat = async (c) => {
    if (!window.confirm(`¿Eliminar la categoría "${c.name}"? No se puede eliminar si tiene beneficios asociados.`)) return;
    try {
      await api.delete(`/admin/benefits/categories/${c.category_id}`);
      toast.success('Categoría eliminada');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15';

  return (
    <div className="space-y-5" data-testid="admin-benefits">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Beneficios para socios</p>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">Descuentos y convenios</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCats(true)}
            data-testid="admin-benefits-manage-cats"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            <Tag className="h-4 w-4" /> Categorías
          </button>
          <button
            onClick={() => setModal({ data: { active: true, featured: false, category: cats[0]?.name || 'Otros', discount_text: '', partner_name: '', title: '' } })}
            className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-sm font-bold text-white hover:bg-orange-600"
            data-testid="admin-benefits-new"
          >
            <Plus className="h-4 w-4" /> Nuevo beneficio
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-cnib-blue" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">Sin beneficios aún.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-benefits-grid">
          {items.map((b) => (
            <div key={b.benefit_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-benefit-${b.benefit_id}`}>
              <div className="flex items-start gap-3">
                {b.partner_logo && <img src={b.partner_logo} alt="" className="h-10 w-10 rounded-xl object-cover" />}
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{b.category} · {b.active ? 'Activo' : 'Pausado'}</p>
                  <p className="truncate text-sm font-extrabold text-slate-900">{b.partner_name}</p>
                  <p className="line-clamp-2 text-xs text-slate-500">{b.title}</p>
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-1">
                <button onClick={() => setModal({ data: b })} className="rounded-full bg-slate-100 p-1.5" data-testid={`admin-benefit-edit-${b.benefit_id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => remove(b.benefit_id)} className="rounded-full bg-red-50 p-1.5 text-red-600" data-testid={`admin-benefit-delete-${b.benefit_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Benefit edit modal */}
      {modal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4" onClick={() => setModal(null)}>
          <div className="w-full max-w-xl overflow-y-auto rounded-3xl bg-white p-5" style={{ maxHeight: '92vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-heading text-lg font-extrabold">{modal.data.benefit_id ? 'Editar beneficio' : 'Nuevo beneficio'}</h3>
              <button onClick={() => setModal(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 space-y-3">
              <input required placeholder="Nombre del partner" value={modal.data.partner_name || ''} onChange={(e) => setModal({ data: { ...modal.data, partner_name: e.target.value } })} className={inputCls} data-testid="admin-benefit-partner" />
              <ImageUploadOrUrl value={modal.data.partner_logo} onChange={(url) => setModal({ data: { ...modal.data, partner_logo: url } })} sizeHint="Logo cuadrado 256×256 px" testid="admin-benefit-logo" />
              <input required placeholder="Título del beneficio" value={modal.data.title || ''} onChange={(e) => setModal({ data: { ...modal.data, title: e.target.value } })} className={inputCls} data-testid="admin-benefit-title" />
              <textarea rows={3} placeholder="Descripción" value={modal.data.description || ''} onChange={(e) => setModal({ data: { ...modal.data, description: e.target.value } })} className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <select value={modal.data.category || 'Otros'} onChange={(e) => setModal({ data: { ...modal.data, category: e.target.value } })} className={inputCls} data-testid="admin-benefit-category">
                  {cats.map((c) => <option key={c.category_id} value={c.name}>{c.name}</option>)}
                </select>
                <input placeholder="Ciudad (opcional)" value={modal.data.city || ''} onChange={(e) => setModal({ data: { ...modal.data, city: e.target.value } })} className={inputCls} />
                <input placeholder="Descuento (ej. 20% OFF)" value={modal.data.discount_text || ''} onChange={(e) => setModal({ data: { ...modal.data, discount_text: e.target.value } })} className={inputCls} />
                <input placeholder="Código (opcional)" value={modal.data.code || ''} onChange={(e) => setModal({ data: { ...modal.data, code: e.target.value } })} className={inputCls} />
              </div>
              <input placeholder="URL de destino (opcional)" value={modal.data.link_url || ''} onChange={(e) => setModal({ data: { ...modal.data, link_url: e.target.value } })} className={inputCls} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={modal.data.active !== false} onChange={(e) => setModal({ data: { ...modal.data, active: e.target.checked } })} /> Activo</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!modal.data.featured} onChange={(e) => setModal({ data: { ...modal.data, featured: e.target.checked } })} /> Destacado</label>
              <button onClick={save} className="w-full rounded-full bg-orange-500 py-2.5 text-sm font-bold text-white hover:bg-orange-600" data-testid="admin-benefit-save">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Category manager modal */}
      {showCats && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4" onClick={() => setShowCats(false)}>
          <div className="w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-5" style={{ maxHeight: '92vh' }} onClick={(e) => e.stopPropagation()} data-testid="admin-benefits-cats-modal">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-heading text-lg font-extrabold flex items-center gap-2"><Tag className="h-5 w-5 text-cnib-blue" /> Categorías de beneficios</h3>
              <button onClick={() => setShowCats(false)} data-testid="admin-benefits-cats-close"><X className="h-5 w-5" /></button>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <input
                value={newCat}
                onChange={(e) => setNewCat(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') createCat(); }}
                placeholder="Nueva categoría (ej. Automotriz)"
                className={inputCls}
                data-testid="admin-benefit-cat-new-input"
              />
              <button
                onClick={createCat}
                disabled={!newCat.trim()}
                data-testid="admin-benefit-cat-new-save"
                className="inline-flex items-center gap-1.5 rounded-xl bg-orange-500 px-4 py-2 text-sm font-bold text-white hover:bg-orange-600 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" /> Agregar
              </button>
            </div>

            <div className="mt-4 space-y-2" data-testid="admin-benefit-cats-list">
              {cats.length === 0 && (
                <p className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-xs text-slate-500">Aún no hay categorías.</p>
              )}
              {cats.map((c) => (
                <div key={c.category_id} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2" data-testid={`admin-benefit-cat-${c.category_id}`}>
                  <GripVertical className="h-4 w-4 text-slate-300" />
                  {editingCatId === c.category_id ? (
                    <input
                      autoFocus
                      value={editingCatName}
                      onChange={(e) => setEditingCatName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEditCat(c); if (e.key === 'Escape') setEditingCatId(null); }}
                      className="flex-1 rounded-lg border border-cnib-blue bg-white px-2 py-1 text-sm outline-none"
                      data-testid={`admin-benefit-cat-input-${c.category_id}`}
                    />
                  ) : (
                    <p className="flex-1 text-sm font-semibold text-slate-800">{c.name}</p>
                  )}
                  {editingCatId === c.category_id ? (
                    <button onClick={() => saveEditCat(c)} className="rounded-full bg-emerald-500 p-1.5 text-white" data-testid={`admin-benefit-cat-save-${c.category_id}`} title="Guardar">
                      <Check className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <button onClick={() => startEditCat(c)} className="rounded-full bg-slate-100 p-1.5" data-testid={`admin-benefit-cat-edit-${c.category_id}`} title="Editar">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button onClick={() => deleteCat(c)} className="rounded-full bg-red-50 p-1.5 text-red-600" data-testid={`admin-benefit-cat-delete-${c.category_id}`} title="Eliminar">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>

            <p className="mt-4 text-xs text-slate-500">
              Al renombrar una categoría, todos los beneficios asociados se actualizan automáticamente. No puedes eliminar una categoría si existen beneficios usándola.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
